import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { getBusinessSummary } from 'lib/api';
import { getBusinessContacts } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import { FieldDefinition, PortfolioTemplate } from 'lib/types/models';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import { CustomHandle } from '..';
import Avals from './Inputs/Avals';
import { Aval } from './Inputs/Avals/types';
import { DateInput } from './Inputs/DateInput';
import { MultiSelect } from './Inputs/MultiSelect';
import OperationAssociation from './Inputs/OperationAssociation';
import { OPERATION_TYPES } from './Inputs/OperationAssociation/operations';
import { SingleSelect } from './Inputs/SingleSelect';
import { TextInput } from './Inputs/TextInput';
import TemplateSelector from './TemplateSelector';
import { useCreateTemplate } from './useCreatePortfolio';

interface Props {
  templates: PortfolioTemplate[];
  businessIdentifier: string | undefined;
}

function FieldRenderer({
  fields,
  field,
  value,
  onChange,
  businessUsers,
  ownUsers,
  businessIdentifier,
}: {
  fields: FieldDefinition[];
  field: FieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  businessUsers: Contact[] | undefined;
  ownUsers: Contact[] | undefined;
  businessIdentifier: string | undefined;
}) {
  const findOptions = () => {
    switch (field.optionsType) {
      case 'business-users':
        return businessUsers?.map((user) => ({
          value: user.id.toString(),
          label: getUserLabel(user),
        }));
      case 'own-users':
        return ownUsers?.map((user) => ({
          value: user.id.toString(),
          label: getUserLabel(user),
        }));
      case 'operation-types':
        return Object.values(OPERATION_TYPES).map((operation) => ({
          value: operation.key,
          label: operation.label,
        }));
    }
  };

  const findOperationType = () => {
    return fields.find((f) => f.id === 'requestType')?.defaultValue as string;
  };

  const getUserLabel = (user: Contact) => {
    if (!user.name) return '';
    const nameParts = user.name.split(' ').filter(Boolean);
    if (nameParts.length === 0) return '';
    if (nameParts.length === 1) return nameParts[0];
    return `${nameParts[0]} ${nameParts[1]}`;
  };

  switch (field.type) {
    case 'text':
      return (
        <TextInput
          value={value as string}
          onChange={onChange}
          placeholder={field.placeholder}
          required={field.required}
          label={field.label}
          helperText={field.helperText}
          disabled={field.disabled}
        />
      );
    case 'date':
      return (
        <DateInput
          value={value as string}
          onChange={(value: string) => onChange(value)}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          helperText={field.helperText}
          disabled={field.disabled}
        />
      );
    case 'select':
      return (
        <SingleSelect
          options={findOptions() ?? []}
          value={value as string}
          onChange={onChange}
          label={field.label}
          placeholder={field.placeholder}
          helperText={field.helperText}
          disabled={field.disabled}
        />
      );
    case 'multiselect':
      return (
        <MultiSelect
          options={findOptions() ?? []}
          value={value as string[]}
          onChange={onChange}
          label={field.label}
          placeholder={field.placeholder}
          helperText={field.helperText}
          min={field.validations?.minLength}
          max={field.validations?.maxLength}
          disabled={field.disabled}
        />
      );
    case 'operation':
      return (
        <OperationAssociation
          value={value as string}
          type={findOperationType()}
          onChange={onChange}
          businessIdentifier={businessIdentifier}
        />
      );
    case 'aval':
      return (
        <Avals
          value={value as Aval[]}
          onChange={onChange}
          businessUsers={businessUsers}
        />
      );
    default:
      return null;
  }
}

const TemplateForm = forwardRef<CustomHandle, Props>(
  ({ templates, businessIdentifier }, ref) => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user);
    const ownIdentifier = user?.businessIdentifier;
    const query = new URLSearchParams({
      isLegalRepresentative: 'true',
    }).toString();
    const { data: { items: businessUsers } = { items: [] } } =
      getBusinessContacts(dispatch, businessIdentifier, query);
    const { data: { items: ownUsers } = { items: [] } } = getBusinessContacts(
      dispatch,
      ownIdentifier,
      query,
    );
    const [template, setTemplate] = useState<PortfolioTemplate | undefined>();
    const [missingSummary, setMissingSummary] = useState<string[] | null>(null);
    const [isCheckingRequiredInfo, setIsCheckingRequiredInfo] = useState(false);
    const fields = template?.signDocumentTemplates[0].fieldMap ?? {};
    const orderedFields = Object.values(fields).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    const [formValues, setFormValues] = useState<Record<string, unknown>>(() =>
      initValuesFromTemplate(fields),
    );
    const { handleCreateTemplate } = useCreateTemplate(
      template,
      businessIdentifier,
      formValues,
    );

    useImperativeHandle(ref, () => ({
      submit: handleCreateTemplate,
    }));

    const fetchSummaryMissingFields = async () => {
      try {
        setIsCheckingRequiredInfo(true);
        if (!businessIdentifier) {
          setMissingSummary(null);
          return;
        }

        const summary = await getBusinessSummary(dispatch, businessIdentifier);
        const fields = [
          { key: 'address', label: 'Dirección' },
          { key: 'commune', label: 'Comuna' },
          { key: 'region', label: 'Región' },
        ] as const;

        const missingFields = fields
          .filter(({ key }) => !summary?.[key])
          .map(({ label }) => label);
        setMissingSummary(missingFields);
      } catch (error) {
        console.error('Error fetching business summary:', error);
        setMissingSummary(null);
      } finally {
        setIsCheckingRequiredInfo(false);
      }
    };

    function initValuesFromTemplate(fields: Record<string, FieldDefinition>) {
      const initial: Record<string, unknown> = {};
      for (const f in fields) {
        const field = fields[f];
        if (field.type === 'multiselect' || field.type === 'aval') {
          initial[f] = field.defaultValue ?? [];
        } else {
          initial[f] = field.defaultValue ?? '';
        }
      }
      return initial;
    }

    function handleChange(fieldId: string, value: unknown) {
      setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    }

    function handleSetTemplate(event: SelectChangeEvent<string>) {
      const templateId = event.target.value;
      const selectedTemplate = templates.find((t) => t.id === templateId);
      setTemplate(selectedTemplate);
    }

    useEffect(() => {
      const initialValues = initValuesFromTemplate(fields);
      setFormValues(initialValues);
    }, [template]);

    useEffect(() => {
      if (!businessIdentifier) return;
      void fetchSummaryMissingFields();
    }, [businessIdentifier]);

    return (
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="template-label">
              {templates.length === 0 ? 'No existen plantillas' : 'Plantilla'}
            </InputLabel>
            <MuiSelect
              labelId="template-label"
              label="Plantilla"
              value={template?.id ?? ''}
              onChange={handleSetTemplate}
              disabled={templates.length === 0}
              error={templates.length === 0}
            >
              {templates.map((templateItem) => (
                <MenuItem key={templateItem.id} value={templateItem.id}>
                  {templateItem.description || 'Sin descripción'}
                </MenuItem>
              ))}
            </MuiSelect>
            <Grid container columnSpacing={2}>
              {orderedFields.map((field) => (
                <Grid item xs={6} key={field.id}>
                  <FieldRenderer
                    fields={Object.values(fields)}
                    field={field}
                    value={formValues[field.id]}
                    onChange={(value: unknown) => handleChange(field.id, value)}
                    businessUsers={businessUsers}
                    ownUsers={ownUsers}
                    businessIdentifier={businessIdentifier}
                  />
                </Grid>
              ))}
            </Grid>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TemplateSelector template={template} />
          {isCheckingRequiredInfo && (
            <Box
              sx={{
                borderRadius: 0.5,
                p: 1.5,
                border: '1px solid #e0e0e0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Revisando que tenga la información necesaria...
                </Typography>
              </Stack>
            </Box>
          )}
          {missingSummary && missingSummary.length > 0 && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 0.5,
                mb: 2,
                backgroundColor: 'rgba(245, 124, 0, 0.08)',
                border: '1px solid rgba(245, 124, 0, 0.2)',
                py: 0.75,
                '& .MuiAlert-message': { py: 0.25 },
              }}
            >
              <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                Información pendiente en la empresa
              </AlertTitle>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
              >
                Faltan datos en el resumen de la empresa:
                <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
                  {missingSummary.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </Box>
              </Typography>
            </Alert>
          )}
        </Grid>
      </Grid>
    );
  },
);

TemplateForm.displayName = 'TemplateForm';
export default TemplateForm;
